import * as THREE from 'three'
import gsap from 'gsap'

import Materials from './Materials.js'
import Shadows from './Shadows.js'
import ModelFactory from './ModelFactory.js'
import Controls from './Controls.js'
import GameState from '../Game/GameState.js'
import SaveSystem from '../Game/SaveSystem.js'
import { getMachinePort, MACHINE_TYPES, TUNING } from '../Game/definitions.js'
import { isWalkable } from '../Game/Navigation.js'
import InteractionTile, { createTextSprite } from './InteractionTile.js'
import DepositView from './DepositView.js'
import DepositIndicator from './DepositIndicator.js'
import MachineIndicator from './MachineIndicator.js'
import MachineView from './MachineView.js'
import BinView from './BinView.js'
import EngineerView from './EngineerView.js'
import InventoryIndicator from './InventoryIndicator.js'
import Sounds from './Sounds.js'
import BeltView from './BeltView.js'
import GroundPileView from './GroundPileView.js'
import BotView from './BotView.js'

export default class World
{
    constructor(_options)
    {
        this.time = _options.time
        this.resources = _options.resources
        this.camera = _options.camera
        this.renderer = _options.renderer
        this.container = new THREE.Group()
        this.state = new GameState(SaveSystem.load())
        this.materials = new Materials({ resources: this.resources })
        this.factory = new ModelFactory({ resources: this.resources, materials: this.materials })
        this.controls = new Controls({ renderer: this.renderer, camera: this.camera })
        this.sounds = new Sounds(this.resources)
        this.machineViews = new Map()
        this.depositViews = new Map()
        this.depositIndicators = new Map()
        this.machineIndicators = new Map()
        this.beltViews = new Map()
        this.groundPileViews = new Map()
        this.botViews = new Map()
        this.flights = new Set()
        this.shadows = new Shadows({ renderer: this.renderer })

        this.container.add(this.factory.createGround())
        this.container.add(this.shadows.container)
        for(const deposit of this.state.deposits)
        {
            const view = new DepositView({ deposit, factory: this.factory })
            this.depositViews.set(deposit.id, view)
            this.container.add(view.container)
            this.shadows.add(view.container)
        }

        this.binView = new BinView({ bin: this.state.bin, factory: this.factory })
        this.container.add(this.binView.container)
        this.shadows.add(this.binView.container)

        this.engineer = new EngineerView({ factory: this.factory })
        this.engineer.container.position.set(this.state.player.x, this.state.player.y, 0)
        this.engineer.syncInventory(this.state.inventory)
        this.container.add(this.engineer.container)
        this.shadows.add(this.engineer.container)
        this.inventoryIndicator = new InventoryIndicator({ time: this.time, factory: this.factory })
        this.container.add(this.inventoryIndicator.container)
        this.camera.player.set(this.state.player.x, this.state.player.y, 0)
        this.camera.target.copy(this.camera.player)
        this.camera.targetEased.copy(this.camera.player)

        this.bindEvents()
        for(const machine of this.state.machines)
            this.addMachine(machine, true)
        for(const belt of this.state.logistics.belts)
            this.addBelt(belt)
        for(const pile of this.state.logistics.groundPiles)
            this.addGroundPile(pile)
        for(const bot of this.state.botSystem.bots)
            this.addBot(bot, true)
        if(this.state.botOrder.revealed)
            this.showBotOrder()
        this.showSite(this.state.activeSite?.revealed ? this.state.activeSite : null)
        gsap.delayedCall(0.45, () => this.revealActiveSite())
        this.time.on('tick', () => this.update())
        this.autosave = new SaveSystem(this.state)
    }

    bindEvents()
    {
        this.state.on('depositChanged', (_deposit) => this.depositViews.get(_deposit.id)?.sync())
        this.state.on('depositRevealed', (_deposit) => this.revealDeposit(_deposit))
        this.state.on('gathered', ({ deposit, resource }) =>
        {
            this.depositIndicators.get(deposit.id)?.dismiss()
            this.depositIndicators.delete(deposit.id)
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.sounds.play('woodHit', 0.4, 240)
            this.fly(resource, new THREE.Vector3(deposit.x, deposit.y, deposit.tree ? 1.8 : 0.8), () => this.playerTarget(), () => this.engineer.syncInventory(this.state.inventory))
        })
        this.state.on('siteTransfer', ({ site, resource }) =>
        {
            this.engineer.syncInventory(this.state.inventory)
            this.sounds.play('area', 0.13, 180)
            this.fly(resource, this.playerTarget(), new THREE.Vector3(site.x, site.y, 0.35), () =>
            {
                if(this.tile?.site.id === site.id)
                    this.tile.sync()
            })
        })
        this.state.on('siteCompleted', ({ site, machine }) =>
        {
            this.showSite(null)
            if(machine)
                this.addMachine(machine)
        })
        this.state.on('siteRevealed', (_site) =>
        {
            gsap.delayedCall(1, () =>
            {
                this.showSite(_site)
                this.revealActiveSite()
            })
        })
        this.state.on('binRevealed', (_bin) => this.revealBin(_bin))
        this.state.on('binInput', ({ bin, resource }) =>
        {
            this.engineer.syncInventory(this.state.inventory)
            this.sounds.play('area', 0.12, 180)
            this.fly(resource, this.playerTarget(), new THREE.Vector3(bin.x, bin.y, 0.9), () => this.binView.sync())
        })
        this.state.on('machineInput', ({ machine, resource }) =>
        {
            const definition = MACHINE_TYPES[machine.type]
            const primaryInput = definition.inputs?.[0]
            const receivedAllInputs = definition.inputs?.length > 0 && definition.inputs.every((_input) => machine.receivedInputs.has(_input))
            if(receivedAllInputs)
            {
                this.machineIndicators.get(machine.id)?.dismiss()
                this.machineIndicators.delete(machine.id)
            }
            else if(definition.kind === 'smelter' && resource === 'coal' && primaryInput)
                this.showMachineIndicator(machine, primaryInput, definition.output)
            this.engineer.syncInventory(this.state.inventory)
            this.sounds.play('area', 0.12, 180)
            const port = getMachinePort(machine, resource === 'coal' || resource === 'timber' ? 'fuel' : 'input', resource)
            this.fly(resource, this.playerTarget(), new THREE.Vector3(port.x, port.y, 0.5), () => this.machineViews.get(machine.id)?.sync())
        })
        this.state.on('machineChanged', (_machine) => this.machineViews.get(_machine.id)?.sync())
        this.state.on('machineProduced', (_machine) => this.machineViews.get(_machine.id)?.sync())
        this.state.on('machineOutput', ({ machine, resource }) =>
        {
            const port = getMachinePort(machine, 'output')
            this.machineViews.get(machine.id)?.sync()
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.sounds.play('area', 0.1, 180)
            this.fly(resource, new THREE.Vector3(port.x, port.y, 0.5), () => this.playerTarget(), () => this.engineer.syncInventory(this.state.inventory))
        })
        this.state.on('beltBuilt', (_belt) => this.addBelt(_belt))
        this.state.on('groundPileRevealed', (_pile) => this.addGroundPile(_pile))
        this.state.on('groundPileInput', ({ pile }) => this.groundPileViews.get(pile.id)?.sync())
        this.state.on('groundPileOutput', ({ pile, resource }) =>
        {
            this.groundPileViews.get(pile.id)?.sync()
            this.engineer.syncInventory(this.state.inventory)
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.sounds.play('area', 0.1, 180)
            this.fly(resource, new THREE.Vector3(pile.x, pile.y, 0.5), () => this.playerTarget())
        })
        this.state.on('beltInput', ({ machine }) => this.machineViews.get(machine.id)?.sync())
        this.state.on('botOrderRevealed', (_site) =>
        {
            this.showBotOrder()
            this.camera.reveal(new THREE.Vector3(_site.x, _site.y, 0), this.engineer.container.position)
        })
        this.state.on('botOrderTransfer', ({ site, resource }) =>
        {
            this.engineer.syncInventory(this.state.inventory)
            this.sounds.play('area', 0.13, 180)
            this.fly(resource, this.playerTarget(), new THREE.Vector3(site.x, site.y, 0.35), () => this.botOrderTile?.sync())
        })
        this.state.on('botOrderCompleted', () => this.botOrderTile?.sync())
        this.state.on('botDeployed', (_bot) => this.addBot(_bot))
        this.state.on('botLoaded', ({ bot, machine }) =>
        {
            this.machineViews.get(machine.id)?.sync()
            this.sounds.play('area', 0.08, 180)
            this.fly('coal', new THREE.Vector3(machine.x, machine.y, 0.7), new THREE.Vector3(bot.x, bot.y, 0.9))
        })
        this.state.on('botUnloaded', ({ bot, machine }) =>
        {
            this.machineViews.get(machine.id)?.sync()
            const port = getMachinePort(machine, 'fuel')
            this.sounds.play('area', 0.08, 180)
            this.fly('coal', new THREE.Vector3(bot.x, bot.y, 0.9), new THREE.Vector3(port.x, port.y, 0.5))
        })
        this.state.on('factoryCompleted', () =>
        {
            this.showSite(null)
            const label = createTextSprite('FACTORY ONLINE  •  FREE PLAY', { background: 'rgba(56, 105, 62, 0.86)' })
            label.position.set(this.state.player.x, this.state.player.y, 3.3)
            this.container.add(label)
            gsap.to(label.material, { opacity: 0, delay: 4, duration: 1, onComplete: () => this.container.remove(label) })
        })
    }

    addMachine(_machine, _restored = false)
    {
        const view = new MachineView({ machine: _machine, factory: this.factory })
        this.machineViews.set(_machine.id, view)
        this.container.add(view.container)
        this.shadows.add(view.container)
        if(_restored)
            view.container.scale.setScalar(1)
        else
        {
            gsap.to(view.container.scale, { x: 1, y: 1, z: 1, duration: 0.65, ease: 'back.out(1.7)' })
            this.sounds.play('reveal', 0.3, 300)
            this.resolveMachineSpawn(_machine)
        }
        const definition = MACHINE_TYPES[_machine.type]
        const inputs = definition.inputs ?? []
        if(definition.kind === 'fabricator' && inputs.length && (!_restored || inputs.some((_input) => !_machine.receivedInputs.has(_input))))
            this.showMachineIndicator(_machine, inputs, definition.output)
    }

    addBelt(_belt)
    {
        if(this.beltViews.has(_belt.id))
            return
        const view = new BeltView({ belt: _belt, state: this.state, factory: this.factory })
        this.beltViews.set(_belt.id, view)
        this.container.add(view.container)
    }

    addGroundPile(_pile)
    {
        if(this.groundPileViews.has(_pile.id))
            return
        const view = new GroundPileView({ pile: _pile, factory: this.factory })
        this.groundPileViews.set(_pile.id, view)
        this.container.add(view.container)
    }

    addBot(_bot, _restored = false)
    {
        if(this.botViews.has(_bot.id))
            return
        const view = new BotView({ bot: _bot, factory: this.factory })
        this.botViews.set(_bot.id, view)
        this.container.add(view.container)
        this.shadows.add(view.container)
        if(!_restored)
        {
            view.container.scale.setScalar(0.01)
            gsap.to(view.container.scale, { x: 1, y: 1, z: 1, duration: 0.55, ease: 'back.out(1.8)' })
            this.sounds.play('reveal', 0.25, 260)
        }
    }

    showBotOrder()
    {
        if(this.botOrderTile)
            return
        this.botOrderTile = new InteractionTile({ time: this.time, site: this.state.botOrder, factory: this.factory, camera: this.camera })
        this.container.add(this.botOrderTile.container)
    }

    showSite(_site)
    {
        if(this.tile)
        {
            this.tile.destroy()
            this.container.remove(this.tile.container)
        }
        this.tile = null
        if(!_site)
            return
        this.tile = new InteractionTile({ time: this.time, site: _site, factory: this.factory, camera: this.camera })
        this.container.add(this.tile.container)
    }

    revealDeposit(_deposit)
    {
        this.depositViews.get(_deposit.id)?.reveal()
        const indicator = new DepositIndicator({ deposit: _deposit, factory: this.factory, camera: this.camera })
        this.depositIndicators.set(_deposit.id, indicator)
        this.container.add(indicator.container)
    }

    showMachineIndicator(_machine, _resource, _output = null)
    {
        if(this.machineIndicators.has(_machine.id))
            return
        const indicator = new MachineIndicator({ machine: _machine, resources: Array.isArray(_resource) ? _resource : [_resource], output: _output, factory: this.factory })
        this.machineIndicators.set(_machine.id, indicator)
        this.container.add(indicator.container)
    }

    revealActiveSite()
    {
        if(!this.state.activeSite?.revealed || !this.tile)
            return
        const site = this.state.activeSite
        this.tile.showPin()
        this.camera.reveal(new THREE.Vector3(site.x, site.y, 0), this.engineer.container.position)
        this.sounds.play('reveal', 0.22, 500)
    }

    revealBin(_bin)
    {
        this.binView.reveal()
        this.camera.reveal(new THREE.Vector3(_bin.x, _bin.y, 0), this.engineer.container.position)
        this.sounds.play('reveal', 0.22, 500)
    }

    playerTarget()
    {
        return new THREE.Vector3(this.engineer.container.position.x, this.engineer.container.position.y, 1.55)
    }

    fly(_resource, _from, _to, _onComplete)
    {
        const item = this.factory.createItem(_resource, 1.2)
        this.container.add(item)
        const flight = { progress: 0 }
        this.flights.add(item)
        const from = typeof _from === 'function' ? _from() : _from
        item.position.copy(from)
        gsap.to(flight, {
            progress: 1,
            duration: 0.48,
            ease: 'power1.inOut',
            onUpdate: () =>
            {
                const to = typeof _to === 'function' ? _to() : _to
                item.position.lerpVectors(from, to, flight.progress)
                item.position.z += Math.sin(flight.progress * Math.PI) * 1.2
                item.rotation.z += 0.12
            },
            onComplete: () =>
            {
                this.container.remove(item)
                this.flights.delete(item)
                _onComplete?.()
            }
        })
    }

    resolveMachineSpawn(_machine)
    {
        const position = this.engineer.container.position
        const delta = new THREE.Vector2(position.x - _machine.x, position.y - _machine.y)
        const distance = delta.length()
        if(distance >= 1.75)
            return
        if(distance < 0.01)
            delta.set(0, -1)
        else
            delta.normalize()
        position.set(_machine.x + delta.x * 1.8, _machine.y + delta.y * 1.8, 0)
        this.state.setPlayerPosition(position.x, position.y)
    }

    isPositionValid(_x, _y)
    {
        return isWalkable(this.state, _x, _y)
    }

    movePlayer(_input, _delta)
    {
        const position = this.engineer.container.position
        if(_input.lengthSq() === 0)
            return false

        const forward = new THREE.Vector2(-1, 1.45).normalize()
        const right = new THREE.Vector2(1.45, 1).normalize()
        const movement = new THREE.Vector2()
            .addScaledVector(right, _input.x)
            .addScaledVector(forward, _input.y)
            .clampLength(0, 1)
            .multiplyScalar(TUNING.playerSpeed * _delta)

        const oldX = position.x
        const oldY = position.y
        const nextX = oldX + movement.x
        const nextY = oldY + movement.y
        if(this.isPositionValid(nextX, nextY))
            position.set(nextX, nextY, 0)
        else
        {
            if(this.isPositionValid(nextX, oldY))
                position.x = nextX
            if(this.isPositionValid(position.x, nextY))
                position.y = nextY
        }

        const moved = Math.hypot(position.x - oldX, position.y - oldY) > 0.0001
        if(moved)
            this.engineer.container.rotation.z = Math.atan2(- movement.x, movement.y)
        return moved
    }

    update()
    {
        const delta = Math.min(this.time.delta / 1000, 0.06)
        const input = this.controls.update()
        const moving = this.movePlayer(input, delta)
        const position = this.engineer.container.position
        this.state.setPlayerPosition(position.x, position.y)
        this.state.update(delta, moving)
        this.camera.player.set(position.x, position.y, 0)
        if(this.state.action && !moving)
        {
            const deposit = this.state.action.deposit
            this.engineer.container.rotation.z = Math.atan2(- (deposit.x - position.x), deposit.y - position.y)
        }
        this.engineer.update(delta, moving, this.state.action)
        this.inventoryIndicator.update(position)
        this.binView.update(this.time.elapsed)
        if(this.tile)
        {
            const site = this.tile.site
            const inside = Math.abs(position.x - site.x) <= TUNING.tileHalfSize && Math.abs(position.y - site.y) <= TUNING.tileHalfSize
            this.tile.update(inside && !moving, this.state.shouldGuideToSite(site))
        }
        for(const view of this.machineViews.values())
            view.update(this.time.elapsed)
        for(const view of this.beltViews.values())
            view.update(delta)
        for(const view of this.groundPileViews.values())
            view.update()
        for(const view of this.botViews.values())
            view.update(delta)
        if(this.botOrderTile)
        {
            const site = this.botOrderTile.site
            const inside = Math.abs(position.x - site.x) <= TUNING.tileHalfSize && Math.abs(position.y - site.y) <= TUNING.tileHalfSize
            this.botOrderTile.update(inside && !moving, this.state.shouldGuideToSite(site))
        }
        for(const indicator of this.depositIndicators.values())
            indicator.update(this.time.elapsed)
        for(const indicator of this.machineIndicators.values())
            indicator.update(this.time.elapsed)
    }
}
